import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/common/SearchBar';

const POPULAR = ['Diabetes Type', 'Hypertension', 'Chest Pain', 'Pneumonia', 'Annual Wellness', 'COVID-19', 'Asthma'];

const CODE_TYPES = [
    { key: 'ICD10',    label: 'ICD-10-CM',  count: '74,260',   desc: 'Diagnosis codes for diseases & conditions', accent: '#0d9488' },
    { key: 'ICD10PCS', label: 'ICD-10-PCS', count: '78,948',   desc: 'Inpatient procedure codes',                accent: '#3b82f6' },
    { key: 'HCPCS',    label: 'HCPCS',      count: '7,000+',   desc: 'Healthcare common procedure codes',        accent: '#8b5cf6' },
    { key: 'NDC',      label: 'NDC',        count: '500,000+', desc: 'National drug codes from FDA',             accent: '#f59e0b' },
    { key: 'LOINC',    label: 'LOINC',      count: '100,000+', desc: 'Lab & clinical observation codes',         accent: '#22c55e' },
    { key: 'RXNORM',   label: 'RxNorm',     count: '100,000+', desc: 'Drug terminology & synonyms',              accent: '#6366f1' },
];

const FEATURES = [
    {
        title: 'Instant Search',
        desc: 'Find any code in milliseconds across all 710,000+ records with real-time suggestions.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
        ),
    },
    {
        title: 'AI Explanations',
        desc: 'Get plain-language explanations of complex medical codes powered by advanced AI.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
        ),
    },
    {
        title: 'Secure & Private',
        desc: 'Your searches are encrypted and private. Your data is never sold to third parties.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
        ),
    },
    {
        title: 'Always Up-to-Date',
        desc: 'Database updated daily with the latest CMS, FDA, and NLM code releases.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
        ),
    },
];

const MARQUEE = ['ICD-10-CM','HCPCS','NDC','LOINC','RxNorm','ICD-10-PCS','Diagnosis Codes','Procedure Codes','Drug Codes','Lab Codes','Free Access','710,000+ Codes','AI Powered','Daily Updates'];

const STATS = [
    { value: '710,000+', label: 'Medical Codes',  sub: 'Across all databases' },
    { value: '6',        label: 'Code Types',     sub: 'In one platform' },
    { value: '100%',     label: 'Free Access',    sub: 'No credit card' },
    { value: 'Daily',    label: 'Updated',        sub: 'From official sources' },
];

const LandingPage = () => {
    const navigate = useNavigate();
    const codesRef = useRef<HTMLDivElement>(null);
    const handleSearch = (term: string) => navigate(`/search?q=${encodeURIComponent(term)}`);

    return (
        <div className="bg-[#030712] overflow-x-hidden">
            <style>{`
                @keyframes marquee { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
                .marquee-track { animation: marquee 28s linear infinite; display: flex; width: max-content; }
                @keyframes float-slow { 0%,100% { transform: translateY(0px) } 50% { transform: translateY(-18px) } }
                @keyframes float-medium { 0%,100% { transform: translateY(0px) } 50% { transform: translateY(-12px) } }
                .float-slow { animation: float-slow 8s ease-in-out infinite; }
                .float-medium { animation: float-medium 6s ease-in-out infinite; }
                @keyframes shimmer { 0% { background-position: -200% center } 100% { background-position: 200% center } }
                .shimmer-text {
                    background: linear-gradient(90deg, #2dd4bf, #5eead4, #99f6e4, #5eead4, #2dd4bf);
                    background-size: 200% auto;
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                    animation: shimmer 4s linear infinite;
                }
                .glass-card {
                    background: rgba(255,255,255,0.03);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255,255,255,0.07);
                }
                .glow-teal { box-shadow: 0 0 60px rgba(20,184,166,0.15), 0 0 120px rgba(20,184,166,0.05); }
            `}</style>

            {/* ── Hero ─────────────────────────────────────── */}
            <section className="relative min-h-screen flex items-center overflow-hidden">
                {/* Background orbs */}
                <div className="absolute top-[-20%] left-[-10%] w-[700px] h-[700px] rounded-full float-slow"
                    style={{ background: 'radial-gradient(circle, rgba(13,148,136,0.12) 0%, transparent 70%)' }}/>
                <div className="absolute bottom-[-15%] right-[-8%] w-[600px] h-[600px] rounded-full float-medium"
                    style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)' }}/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.04) 0%, transparent 60%)' }}/>

                {/* Grid */}
                <div className="absolute inset-0"
                    style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '72px 72px' }}/>

                <div className="relative w-full max-w-5xl mx-auto px-6 py-32 text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2.5 border border-teal-500/20 bg-teal-500/5 text-teal-400 text-xs font-semibold px-5 py-2 rounded-full mb-10 backdrop-blur-sm">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"/>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-400"/>
                        </span>
                        710,000+ Medical Codes 
                    </div>

                    {/* Headline */}
                    <h1 className="text-5xl sm:text-6xl md:text-[5.5rem] font-black text-white leading-[1.02] tracking-[-0.02em] mb-7">
                        Find Any<br/>
                        <span className="shimmer-text">Medical Code</span><br/>
                        <span className="text-gray-300">Instantly</span>
                    </h1>

                    <p className="text-base text-gray-500 mb-14 max-w-lg mx-auto leading-relaxed">
                        Search ICD-10, HCPCS, NDC, LOINC & RxNorm codes in one place.
                        Built for medical billers, coders & healthcare professionals.
                    </p>

                    {/* Search box */}
                    <div className="glass-card rounded-2xl p-1.5 max-w-2xl mx-auto mb-6 glow-teal">
                        <div className="p-3.5">
                            <SearchBar onSearch={handleSearch} placeholder="Search diagnosis, condition, drug, or code..."/>
                        </div>
                        <div className="flex items-center gap-2 px-4 pb-3.5 flex-wrap">
                            <span className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.15em]">Trending:</span>
                            {POPULAR.map(term => (
                                <button key={term} onClick={() => handleSearch(term)}
                                    className="text-[11px] px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-gray-500 hover:border-teal-500/40 hover:text-teal-300 hover:bg-teal-500/5 transition-all duration-200">
                                    {term}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* CTAs */}
                    {/* <div className="flex items-center justify-center gap-3 flex-wrap">
                        <button onClick={() => navigate('/register')}
                            className="group relative px-8 py-3.5 text-white text-sm font-bold rounded-xl transition-all duration-200 overflow-hidden"
                            style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)' }}>
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}/>
                            <span className="relative flex items-center gap-2">
                                Get Started — Free
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-0.5 transition-transform duration-200">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </span>
                        </button>
                        <button onClick={() => codesRef.current?.scrollIntoView({ behavior: 'smooth' })}
                            className="px-8 py-3.5 bg-white/[0.05] hover:bg-white/[0.08] text-gray-400 hover:text-gray-200 text-sm font-bold rounded-xl border border-white/[0.08] hover:border-white/[0.14] transition-all duration-200">
                            Explore Codes
                        </button>
                    </div>

                    {/* Trust line */}
                     {/* <p className="mt-7 text-[11px] text-gray-700 tracking-wide">
                        No credit card required &nbsp;·&nbsp; 2 free searches/day &nbsp;·&nbsp; Upgrade anytime
                    </p>  */}
                </div>
            </section>

            {/* ── Marquee ──────────────────────────────────── */}
            <div className="relative overflow-hidden py-3.5 border-y border-white/[0.05]"
                style={{ background: 'linear-gradient(90deg, rgba(13,148,136,0.15), rgba(13,148,136,0.08), rgba(13,148,136,0.15))' }}>
                <div className="marquee-track">
                    {[...MARQUEE, ...MARQUEE].map((item, i) => (
                        <span key={i} className="inline-flex items-center gap-3.5 mx-7 text-[10px] font-bold text-teal-400/70 uppercase tracking-[0.18em] whitespace-nowrap">
                            {item}
                            <span className="w-1 h-1 rounded-full bg-teal-600/60"/>
                        </span>
                    ))}
                </div>
            </div>

            {/* ── Stats ────────────────────────────────────── */}
            <section className="py-20 border-b border-white/[0.05]">
                <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 sm:grid-cols-4 gap-1">
                    {STATS.map((s, i) => (
                        <div key={i} className="group text-center px-8 py-8 relative cursor-default">
                            {i < STATS.length - 1 && (
                                <div className="hidden sm:block absolute right-0 top-1/4 bottom-1/4 w-px bg-white/[0.06]"/>
                            )}
                            <div className="text-4xl font-black mb-1.5 transition-colors duration-300"
                                style={{ color: 'rgba(255,255,255,0.9)' }}>
                                <span className="group-hover:text-teal-400 transition-colors duration-300">{s.value}</span>
                            </div>
                            <div className="text-sm font-semibold text-gray-400 mb-1">{s.label}</div>
                            <div className="text-[11px] text-gray-700">{s.sub}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Code Types ───────────────────────────────── */}
            <section ref={codesRef} className="py-28 relative overflow-hidden">
                <div className="absolute inset-0"
                    style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)', backgroundSize: '56px 56px' }}/>
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/20 to-transparent"/>

                <div className="relative max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 text-teal-500 text-[10px] font-black tracking-[0.3em] uppercase mb-5">
                            <div className="w-8 h-px bg-teal-500/50"/>
                            Code Databases
                            <div className="w-8 h-px bg-teal-500/50"/>
                        </div>
                        <h2 className="text-4xl sm:text-5xl font-black text-white mb-5 leading-tight tracking-tight">
                            All Code Types<br/>
                            <span className="shimmer-text">in One Place</span>
                        </h2>
                        <p className="text-gray-600 text-sm max-w-md mx-auto leading-relaxed">
                            Browse and search across 6 major medical code databases — no switching between websites.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {CODE_TYPES.map((ct) => (
                            <button key={ct.key} onClick={() => navigate(`/codes/${ct.key}`)}
                                className="group relative rounded-2xl p-7 text-left transition-all duration-300 overflow-hidden border border-white/[0.06] hover:border-white/[0.12]"
                                style={{ background: 'rgba(255,255,255,0.02)' }}>
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                                    style={{ background: `radial-gradient(ellipse at 15% 15%, ${ct.accent}14 0%, transparent 60%)` }}/>
                                <div className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    style={{ background: `linear-gradient(90deg, transparent 10%, ${ct.accent}60, transparent 90%)` }}/>

                                <div className="relative">
                                    <div className="flex items-start justify-between mb-7">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: `${ct.accent}12`, border: `1px solid ${ct.accent}25` }}>
                                            <div className="w-2.5 h-2.5 rounded-full transition-all duration-300 group-hover:scale-110"
                                                style={{ backgroundColor: ct.accent, boxShadow: `0 0 8px ${ct.accent}60` }}/>
                                        </div>
                                        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/[0.06] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0.5">
                                            <svg className="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                                <path d="M5 12h14M12 5l7 7-7 7"/>
                                            </svg>
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-bold text-white mb-1.5 transition-colors duration-300"
                                        style={{ color: undefined }}
                                        onMouseEnter={() => {}} >
                                        <span className="group-hover:text-teal-300 transition-colors duration-300">{ct.label}</span>
                                    </h3>
                                    <p className="text-[11px] font-mono mb-3.5 transition-colors duration-300"
                                        style={{ color: ct.accent + 'aa' }}>
                                        {ct.count} codes
                                    </p>
                                    <p className="text-[13px] text-gray-600 leading-relaxed group-hover:text-gray-500 transition-colors duration-300">
                                        {ct.desc}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Features ─────────────────────────────────── */}
            <section className="py-28 px-6 relative">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-teal-950/10 to-transparent pointer-events-none"/>
                <div className="relative max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 text-teal-500 text-[10px] font-black tracking-[0.3em] uppercase mb-5">
                            <div className="w-8 h-px bg-teal-500/50"/>
                            Why Us
                            <div className="w-8 h-px bg-teal-500/50"/>
                        </div>
                        <h2 className="text-4xl sm:text-5xl font-black text-white mb-4 tracking-tight">
                            Built for Professionals
                        </h2>
                        <p className="text-gray-600 text-sm max-w-sm mx-auto">Everything you need, nothing you don't.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {FEATURES.map((f, i) => (
                            <div key={i}
                                className="group relative rounded-2xl p-8 border border-white/[0.06] hover:border-teal-500/20 transition-all duration-300 overflow-hidden cursor-default"
                                style={{ background: 'rgba(255,255,255,0.02)' }}>
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                                    style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(20,184,166,0.06) 0%, transparent 65%)' }}/>
                                <div className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    style={{ background: 'linear-gradient(90deg, transparent 10%, rgba(20,184,166,0.5), transparent 90%)' }}/>

                                <div className="relative">
                                    <div className="w-11 h-11 rounded-xl border border-white/[0.08] group-hover:border-teal-500/25 flex items-center justify-center text-gray-600 group-hover:text-teal-400 transition-all duration-300 mb-6"
                                        style={{ background: 'rgba(255,255,255,0.03)' }}>
                                        {f.icon}
                                    </div>
                                    <h3 className="text-base font-bold text-white mb-2.5">{f.title}</h3>
                                    <p className="text-[13px] text-gray-600 leading-relaxed group-hover:text-gray-500 transition-colors duration-300">{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ──────────────────────────────────────── */}
            <section className="relative py-28 px-6 overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/20 to-transparent"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full"
                    style={{ background: 'radial-gradient(ellipse, rgba(13,148,136,0.1) 0%, transparent 65%)' }}/>

                <div className="relative max-w-2xl mx-auto text-center">
                    

                    <h2 className="text-4xl sm:text-5xl font-black text-white mb-5 leading-tight tracking-tight">
                        Start Searching<br/>
                        <span className="shimmer-text">for Free Today</span>
                    </h2>
                    <p className="text-gray-600 text-sm mb-10 max-w-sm mx-auto leading-relaxed">
                        2 free searches per day on a free account. Upgrade anytime for unlimited access.
                    </p>

                    <div className="flex items-center justify-center gap-3 flex-wrap">
                        <button onClick={() => navigate('/register')}
                            className="group relative px-9 py-4 text-white text-sm font-bold rounded-xl transition-all duration-200 overflow-hidden"
                            style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: '0 0 40px rgba(13,148,136,0.25)' }}>
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}/>
                            <span className="relative flex items-center gap-2">
                                Create Free Account
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-0.5 transition-transform duration-200">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </span>
                        </button>
                        <button onClick={() => navigate('/login')}
                            className="px-9 py-4 bg-white/[0.05] hover:bg-white/[0.08] text-gray-400 hover:text-gray-200 text-sm font-bold rounded-xl border border-white/[0.08] hover:border-white/[0.14] transition-all duration-200">
                            Sign In
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LandingPage;
